import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Container, Badge, Spinner, Row, Col, Button, Offcanvas } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaSearch, 
  FaChevronRight, 
  FaInfoCircle, 
  FaClock,
  FaFilter,
  FaCalendarPlus,
  FaArrowRight
} from "react-icons/fa";
import axios from "axios";

// Components
import Header from "../components/Header";
import Footer from "../components/Footer";
import API_BASE_URL from "../config";

const ImportantDates = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  // --- DATA FETCHING ---
  const fetchData = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    else setIsSyncing(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fetch/admissionDatesData`);
      if (response.data?.[0]?.admissionCalendar) {
        setData(response.data[0].admissionCalendar);
      }
    } catch (err) {
      console.error("Sync error. Using local cache.");
    } finally {
      setLoading(false);
      setIsSyncing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  // --- FILTERING ---
  const filteredData = useMemo(() => 
    data.filter(item => item.programName.toLowerCase().includes(searchTerm.toLowerCase())),
  [searchTerm, data]);

  const current = filteredData[activeIdx] || null;

  const handleSelect = (idx) => {
    setActiveIdx(idx);
    setShowDrawer(false);
  };

  if (loading) return (
    <div className="premium-loader">
      <Spinner animation="border" variant="primary" />
      <p className="mt-3 fw-bold letter-spacing-2">SYNCING CALENDAR</p>
    </div>
  );

  return (
    <div className="portal-master-wrapper">
      <Header />

      {/* Replicated Sub-Nav Styling */}
      <section className="portal-sub-nav py-4 shadow-sm bg-white sticky-top" style={{ top: '0', zIndex: 1020 }}>
        <Container>
          <Row className="align-items-center">
            <Col md={5}>
              <h1 className="h4 fw-900 text-navy m-0">Admission <span className="text-primary">Calendar</span></h1>
              <small className="text-muted d-flex align-items-center gap-2">
                 <span className="sync-dot-mini"></span> {isSyncing ? "Syncing..." : "Live Schedule"}
              </small>
            </Col>
            <Col md={7}>
              <div className="d-flex gap-3 justify-content-end align-items-center mt-3 mt-md-0">
                <div className="search-pill-container flex-grow-1">
                  <FaSearch className="search-icon" />
                  <input type="text" placeholder="Search programs..." onChange={(e) => { setSearchTerm(e.target.value); setActiveIdx(0); }} />
                </div>
                {/* Mobile Filter Toggle */}
                <Button 
                    variant="primary" 
                    className="rounded-pill px-4 d-lg-none d-flex align-items-center gap-2 nav-hint-pulse shadow-sm h-100"
                    onClick={() => setShowDrawer(true)}
                >
                    <FaFilter /> <span className="fw-bold small">Select Track</span>
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="portal-viewport bg-light-soft py-5">
        <Container className="main-portal-container section-standard-spacing position-relative" style={{ zIndex: 10 }}>
          <Row className="g-4">
            {/* Desktop Sidebar */}
            <Col lg={4} className="d-none d-lg-block">
              <div className="sticky-sidebar">
                <div className="sidebar-card border-0 shadow-sm">
                  <div className="card-label">ACADEMIC TRACKS</div>
                  <div className="program-list custom-scrollbar">
                    {filteredData.map((prog, i) => (
                      <button 
                        key={i} 
                        className={`prog-item w-100 d-flex align-items-center justify-content-between p-3 border-0 bg-transparent transition-all ${activeIdx === i ? 'is-active' : ''}`} 
                        onClick={() => handleSelect(i)}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div className={`icon-circle-mini ${activeIdx === i ? 'active' : ''}`}>
                            <FaCalendarPlus />
                          </div>
                          <div className="text-start">
                              <div className="name fw-bold mb-0">{prog.programName}</div>
                              <div className="stats-tag">Session {prog.year}</div>
                          </div>
                        </div>
                        <FaChevronRight className={`chevron-mini ${activeIdx === i ? 'visible' : ''}`} />
                      </button>
                    ))}
                    {filteredData.length === 0 && (
                      <div className="p-4 text-center text-muted small">No programs found.</div>
                    )}
                  </div>
                </div>
              </div>
            </Col>

            {/* Main Content Area */}
            <Col lg={8}>
              <AnimatePresence mode="wait">
                {current ? (
                  <motion.div key={current.programName} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="details-card bg-white p-4 p-lg-5 rounded-4 shadow-sm">
                    <div className="section-intro mb-5">
                      <div className="category-tag">Official Timeline</div>
                      <h2 className="portal-heading text-navy display-5 mb-3">{current.programName}</h2>
                      <div className="ivy-divider mb-4" />
                      <div className="d-flex align-items-center gap-3">
                         <Badge bg="soft-primary" className="text-primary px-3 py-2 rounded-pill fw-bold">Session {current.year}</Badge>
                         <p className="small text-muted mb-0">Admission milestones and deadlines</p>
                      </div>
                    </div>

                    {current.phases.map((phase, pIdx) => (
                      <div key={pIdx} className="phase-block bg-white p-4 p-lg-5 rounded-4 shadow-sm mb-4 border-top border-primary border-4">
                        <h3 className="h5 fw-800 text-primary mb-4 text-uppercase letter-spacing-1">{phase.phaseName}</h3>
                        
                        <div className="timeline-v-stack">
                          {phase.dates.map((dateObj, dIdx) => (
                            <div key={dIdx} className="milestone-row d-flex flex-column flex-md-row justify-content-between align-items-md-center py-3 border-bottom-dashed gap-3">
                              <div className="d-flex align-items-center">
                                <div className="dot-indicator me-3" />
                                <div className="fw-700 text-navy">{dateObj.label}</div>
                              </div>
                              <div className="date-display d-flex align-items-center gap-2">
                                <FaClock className="text-primary small" />
                                <span>{dateObj.date}</span>
                              </div>
                            </div>
                          ))}
                        </div>

                        {phase.notes && phase.notes.length > 0 && (
                          <div className="notes-box bg-light mt-4 p-3 rounded-3 border-start border-warning border-4">
                            <h6 className="fw-800 text-navy small mb-3"><FaInfoCircle className="me-2 text-warning"/> NOTES & REQUIREMENTS</h6>
                            {phase.notes.map((note, nIdx) => (
                              <div key={nIdx} className="d-flex gap-2 mb-2">
                                <FaArrowRight className="text-warning mt-1 small" style={{ minWidth: '12px' }} />
                                <p className="small text-muted mb-0">{note}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </motion.div>
                ) : (
                  <div className="text-center py-5 bg-white rounded-4 shadow-sm details-card h-100 d-flex flex-column align-items-center justify-content-center border-dashed">
                    <FaInfoCircle size={50} className="text-muted mb-3 opacity-20" />
                    <h4 className="text-muted fw-bold">Select Program</h4>
                    <p className="text-muted small px-4">Choose an academic track from the sidebar to view its admission calendar.</p>
                  </div>
                )}
              </AnimatePresence>
            </Col>
          </Row>
        </Container>
      </section>

      {/* --- MOBILE TRACK DRAWER --- */}
      <Offcanvas show={showDrawer} onHide={() => setShowDrawer(false)} placement="bottom" className="h-75 rounded-top-4">
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="fw-bold text-navy">Select Academic Track</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0 bg-light">
          <div className="program-list-mobile">
              {filteredData.map((prog, i) => (
                <button 
                key={i} 
                className={`prog-item mobile-item w-100 d-flex align-items-center justify-content-between p-3 border-0 bg-transparent transition-all ${activeIdx === i ? 'is-active' : ''}`} 
                onClick={() => handleSelect(i)}
                >
                <div className="d-flex align-items-center gap-3">
                    <div className={`icon-circle-mini ${activeIdx === i ? 'active' : ''}`}>
                         <FaCalendarPlus />
                    </div>
                    <div className="text-start">
                        <div className="name fw-bold mb-0">{prog.programName}</div>
                        <div className="stats-tag">Session {prog.year}</div>
                    </div>
                </div>
                {activeIdx === i && <FaChevronRight className="chevron-mini visible" />}
                </button>
              ))}
           </div>
        </Offcanvas.Body>
      </Offcanvas>

      <Footer />

      <style>{`
        .portal-master-wrapper { background-color: #fff; min-height: 100vh; font-family: 'Inter', sans-serif; }
        .bg-light-soft { background-color: #f8faff; }
        .fw-900 { font-weight: 900; }
        .text-navy { color: #002b5a; }
        .ls-1 { letter-spacing: 1px; }

        /* Typography Sync */
        .category-tag { color: #0055a4; font-weight: 800; text-transform: uppercase; font-size: 0.8rem; letter-spacing: 2px; }
        .portal-heading { font-weight: 900; letter-spacing: -1.5px; line-height: 1.1; }
        .ivy-divider { width: 60px; height: 5px; background: #0055a4; border-radius: 10px; }

        .sync-dot-mini {
          width: 8px; height: 8px; background: #10b981;
          border-radius: 50%; display: inline-block;
          animation: pulse-green 2s infinite;
        }
        @keyframes pulse-green {
          0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
          70% { transform: scale(1); box-shadow: 0 0 0 5px rgba(16, 185, 129, 0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
        }

        .main-portal-container { margin-top: 0; padding-bottom: 100px; position: relative; z-index: 10; }
        
        .search-pill-container {
          background: #f1f4f9; border-radius: 50px; padding: 10px 25px;
          display: flex; align-items: center; transition: 0.3s;
        }
        .search-pill-container:focus-within { background: #fff; box-shadow: 0 10px 25px rgba(0,0,0,0.05); border: 1px solid #0055a4; }
        .search-pill-container input { border: none; background: transparent; outline: none; width: 100%; margin-left: 12px; font-weight: 500; color: #002b5a; }
        .search-icon { color: #94a3b8; }

        /* Sidebar Styling */
        .sticky-sidebar { position: sticky; top: 120px; z-index: 100; transition: top 0.3s; }
        .sidebar-card { background: #fff; border-radius: 24px; overflow: hidden; display: flex; flex-direction: column; max-height: 75vh; border: 1px solid #e2e8f0 !important; }
        
        .card-label { 
          padding: 18px 25px; background: #002b5a; color: white; 
          font-weight: 800; font-size: 0.75rem; letter-spacing: 2px; text-transform: uppercase;
        }

        .program-list { overflow-y: auto; flex: 1; }
        .custom-scrollbar::-webkit-scrollbar { width: 5px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }

        .prog-item {
          transition: 0.3s;
          border-bottom: 1px solid #f1f5f9 !important;
          cursor: pointer;
        }
        .prog-item:hover { background-color: #f8fafc !important; }
        .prog-item.is-active {
          background-color: #002b5a !important;
          border-left: 6px solid #ffc300 !important;
        }
        .prog-item.is-active .name { color: white !important; }
        .prog-item.is-active .stats-tag { color: rgba(255,255,255,0.7) !important; }
        .prog-item.is-active .icon-circle-mini { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); color: white; }

        .icon-circle-mini {
          width: 42px; height: 42px; background: #f8fafc; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.1rem; color: #64748b; transition: 0.3s;
          border: 1px solid #e2e8f0;
        }
        .icon-circle-mini.active { background: #002b5a; color: #fff; border-color: #002b5a; }

        .stats-tag { font-size: 0.75rem; color: #94a3b8; font-weight: 600; }
        .chevron-mini { font-size: 0.8rem; color: #cbd5e0; opacity: 0; transition: 0.3s; transform: translateX(-10px); }
        .chevron-mini.visible { opacity: 1; transform: translateX(0); color: #0055a4; }
        .prog-item.is-active .chevron-mini.visible { color: #ffc300; }

        .details-card { border-radius: 24px !important; border: 1px solid #e2e8f0 !important; }

        .phase-block { border-top: 4px solid #0055a4 !important; }
        .border-bottom-dashed { border-bottom: 1px dashed #e2e8f0; }
        
        .dot-indicator { width: 8px; height: 8px; border-radius: 50%; background: #cbd5e0; }
        .milestone-row:hover .dot-indicator { background: #0055a4; transform: scale(1.5); }
        .milestone-row { transition: 0.2s; border-radius: 12px; }
        .milestone-row:hover { background: #f8faff; padding-left: 10px; padding-right: 10px; }

        .date-display {
          background: #f0f4f8; color: #0055a4; padding: 6px 16px;
          border-radius: 50px; font-weight: 800; font-size: 0.8rem;
        }

        .premium-loader { height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #fff; }

        @media (max-width: 991px) {
          .main-portal-container { margin-top: 0px; }
          .section-standard-spacing { padding-bottom: 30px; }
          .portal-heading { font-size: 1.6rem; word-break: break-word; letter-spacing: -1px; }
          .category-tag { font-size: 0.7rem; letter-spacing: 1.5px; }
          .details-card { padding: 1rem !important; }
          .phase-block { padding: 1.25rem !important; }
          
          .program-list-mobile .prog-item.mobile-item { border-bottom: 1px solid #f1f5f9 !important; padding: 15px; }
          .program-list-mobile .prog-item.mobile-item.is-active { 
            background: #002b5a !important; 
            color: white !important; 
            border-left: 5px solid #ffc300 !important; 
          }
          .program-list-mobile .prog-item.mobile-item.is-active .name { color: white !important; }
          .program-list-mobile .prog-item.mobile-item.is-active .stats-tag { color: rgba(255,255,255,0.7) !important; }
          .program-list-mobile .prog-item.mobile-item.is-active .icon-circle-mini { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); color: white; }
          .sticky-sidebar { position: relative !important; top: 0 !important; }
        }

        @keyframes hintPulse {
          0% { box-shadow: 0 0 0 0 rgba(0, 85, 164, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(0, 85, 164, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 85, 164, 0); }
        }
        .nav-hint-pulse {
          animation: hintPulse 2s infinite;
          border: 2px solid #fff !important;
        }
      `}</style>
    </div>
  );
};

export default ImportantDates;